/**
 * SITE CONTEXT
 * Provides site-specific configuration throughout the app
 * Enables single codebase to power multiple domains
 * NOW WITH DYNAMIC DATA FETCHING FROM API
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { getDoctorInfo as fetchDoctorInfo } from '../services/api';

const SiteContext = createContext(null);

// ============================================
// STATIC CONFIGURATION DATA (Moved from siteConfig.js)
// ============================================

// Theme configurations for each site
const THEMES = {
  main: {
    primary: 'cyan-700',
    primaryHex: '#0e7490',
    accent: 'teal-500',
    accentHex: '#14b8a6',
    lightBgHex: '#ecfeff',
    textClass: 'text-cyan-700',
    bgClass: 'bg-cyan-700',
    borderClass: 'border-cyan-700',
    hoverBg: 'hover:bg-cyan-800',
    lightBg: 'bg-cyan-50',
    gradient: 'from-cyan-600 to-teal-500',
    bgGradient: 'bg-gradient-to-r from-cyan-600 to-teal-500'
  },
  shoulder: {
    primary: 'orange-600',
    primaryHex: '#ea580c',
    accent: 'amber-500',
    accentHex: '#f59e0b',
    lightBgHex: '#fff7ed',
    textClass: 'text-orange-600',
    bgClass: 'bg-orange-600',
    borderClass: 'border-orange-600',
    hoverBg: 'hover:bg-orange-700',
    lightBg: 'bg-orange-50',
    gradient: 'from-orange-600 to-amber-500',
    bgGradient: 'bg-gradient-to-r from-orange-600 to-amber-500'
  },
  elbow: {
    primary: 'slate-700',
    primaryHex: '#334155',
    accent: 'slate-500',
    accentHex: '#64748b',
    lightBgHex: '#f8fafc',
    textClass: 'text-slate-700',
    bgClass: 'bg-slate-700',
    borderClass: 'border-slate-700',
    hoverBg: 'hover:bg-slate-800',
    lightBg: 'bg-slate-50',
    gradient: 'from-slate-700 to-slate-500',
    bgGradient: 'bg-gradient-to-r from-slate-700 to-slate-500'
  },
  handandwrist: {
    primary: 'green-700',
    primaryHex: '#15803d',
    accent: 'emerald-500',
    accentHex: '#10b981',
    lightBgHex: '#f0fdf4',
    textClass: 'text-green-700',
    bgClass: 'bg-green-700',
    borderClass: 'border-green-700',
    hoverBg: 'hover:bg-green-800',
    lightBg: 'bg-green-50',
    gradient: 'from-green-700 to-emerald-500',
    bgGradient: 'bg-gradient-to-r from-green-700 to-emerald-500'
  }
};

// Doctor information (static fallback, API provides dynamic data)
const DOCTOR_INFO = {
  name: 'Dr. Deepthi Nandan Reddy',
  shortName: 'Dr. DN Reddy',
  nameTelugu: 'డాక్టర్ దీప్తి నందన్ రెడ్డి',
  credentials: 'MBBS, MS (Orthopaedics), MSc Orthopaedic Engineering (Cardiff, UK), FRCS (Trauma & Ortho, Edinburgh), FRCS (Ortho, UK), CCT (UK)',
  shortCredentials: 'MBBS, MS, FRCS (UK), CCT',
  title: 'Upper Limb & Sports Injury Orthopaedic Surgeon',
  subtitle: 'Fellowship-trained in Shoulder, Elbow, Hand & Wrist Surgery',
  fellowships: 'UK NHS & USA (Mayo Clinic, Florida Orthopaedic Institute)',
  specialization: 'Upper Limb Surgery',
  tagline: 'Restoring Mobility, Transforming Lives',
  experience: `${new Date().getFullYear() - 1991}+ years of experience`,
  experienceYears: new Date().getFullYear() - 1991,
  phone: '+91 99599 64567',
  email: 'info@drdnreddyortho.com',
  whatsapp: '+919959964567',
  whatsappLink: 'https://wa.me/919959964567',
  hospital: 'Apollo Hospitals',
  hospitalFull: 'Apollo Hospitals, Financial District',
  address: 'D.No-2-105/6/SE, Financial District, Nanakramguda, Serilingampally, Hyderabad, Telangana 500032',
  addressShort: 'Financial District, Hyderabad',
  timings: 'Open 24 hours',
  rating: '5.0',
  reviewCount: '143',
  consultationLink: 'https://www.apollohospitals.com/book-appointment/',
  googleMapsLink: 'https://maps.app.goo.gl/7SHNYCJaRLxvTRTV6',
  hospitalPhone: '040 6907 1222',
  doctorImage: 'https://customer-assets.emergentagent.com/job_664f5b05-f9bf-427d-869f-0304c630b7f5/artifacts/d8vss7a1_e146c649-5e7e-46df-a58e-0e1fdc565827.avif',
  patients_treated: '15000+',
  surgeries_performed: '5000+',
  leadershipRoles: [
    { role: 'President', organization: 'Shoulder & Elbow Society of India (SESI)', period: 'Current' }
  ],
  keyDistinctions: [
    'UK Certificate of Completion of Training (CCT)',
    'FRCS (Royal College of Surgeons, Edinburgh)',
    'FRCS (Orthopaedics, UK)',
    'Mayo Clinic Travelling Fellowship (BSES Award, 2012)'
  ],
  academicHighlights: [],
  fellowshipDetails: [
    { institution: 'Mayo Clinic', location: 'USA', year: '2012', specialty: 'Upper Limb Surgery' },
    { institution: 'Florida Orthopaedic Institute', location: 'USA', specialty: 'Shoulder & Elbow' },
    { institution: 'Wrightington Hospital', location: 'UK', specialty: 'Upper Limb' }
  ],
  positions: [
    { title: 'Senior Consultant', organization: 'Apollo Hospitals, Financial District', type: 'Current' }
  ],
  memberships: [
    'British Shoulder & Elbow Surgeons (BSES)',
    'Indian Shoulder & Elbow Society',
    'Indian Orthopaedic Association (IOA)'
  ],
  keyProcedures: {
    shoulder: ['Arthroscopy', 'Rotator Cuff Repair', 'Bankart Repair', 'Latarjet', 'Replacement'],
    elbow: ['Arthroscopy', 'Ligament Reconstruction', 'Tennis Elbow', 'Replacement'],
    handwrist: ['Wrist Arthroscopy', 'Carpal Tunnel', 'TFCC Repair', 'Dupuytren\'s']
  },
  socialMedia: {
    youtube: 'https://www.youtube.com/@DrDNReddy'
  }
};

// Site-specific configurations
const SITE_CONFIGS = {
  main: {
    id: 'main',
    name: 'Dr. DN Reddy',
    domain: 'drdnreddyortho.com',
    title: 'Upper Limb & Sports Injury Surgeon',
    subtitle: 'Senior Consultant Orthopaedic Surgeon',
    metaDescription: 'Dr. Deepthi Nandan Reddy - Fellowship-trained Upper Limb Surgeon. UK CCT, FRCS, Mayo Clinic Fellow. Founder & Past President, SESI. Expert in shoulder, elbow, hand & wrist surgery. Hyderabad.',
    theme: THEMES.main,
    conditions: [],
    symptoms: [],
    relatedSites: ['shoulder', 'elbow', 'handandwrist']
  },
  shoulder: {
    id: 'shoulder',
    name: 'Shoulder Care',
    domain: 'shoulder.co.in',
    title: 'Shoulder Specialist',
    subtitle: 'Expert Shoulder Surgery',
    metaDescription: 'Expert shoulder surgery by Dr. Deepthi Nandan Reddy. Rotator cuff repair, shoulder replacement, arthroscopy. Hyderabad.',
    theme: THEMES.shoulder,
    conditions: [],
    symptoms: [],
    relatedSites: ['main', 'elbow', 'handandwrist']
  },
  elbow: {
    id: 'elbow',
    name: 'Elbow Care',
    domain: 'elbow.co.in',
    title: 'Elbow Specialist',
    subtitle: 'Expert Elbow Surgery',
    metaDescription: 'Expert elbow surgery by Dr. Deepthi Nandan Reddy. Tennis elbow, elbow replacement, arthroscopy. Hyderabad.',
    theme: THEMES.elbow,
    conditions: [],
    symptoms: [],
    relatedSites: ['main', 'shoulder', 'handandwrist']
  },
  handandwrist: {
    id: 'handandwrist',
    name: 'Hand & Wrist Care',
    domain: 'handandwrist.co.in',
    title: 'Hand & Wrist Specialist',
    subtitle: 'Expert Hand & Wrist Surgery',
    metaDescription: 'Expert hand & wrist surgery by Dr. Deepthi Nandan Reddy. Carpal tunnel, trigger finger, wrist arthroscopy. Hyderabad.',
    theme: THEMES.handandwrist,
    conditions: [],
    symptoms: [],
    relatedSites: ['main', 'shoulder', 'elbow']
  }
};

// Function to get site config based on environment
const getSiteConfig = () => {
  const siteId = process.env.REACT_APP_SITE_ID || 'main';
  return SITE_CONFIGS[siteId] || SITE_CONFIGS.main;
};

// ============================================
// END STATIC CONFIGURATION
// ============================================

// Transform API doctor data to match frontend expected format
const transformDoctorData = (apiData) => {
  if (!apiData) return null;
  
  return {
    name: apiData.name || DOCTOR_INFO.name,
    shortName: apiData.short_name || DOCTOR_INFO.shortName,
    nameTelugu: apiData.name_telugu || DOCTOR_INFO.nameTelugu,
    credentials: apiData.credentials || DOCTOR_INFO.credentials,
    shortCredentials: apiData.short_credentials || DOCTOR_INFO.shortCredentials,
    title: apiData.title || DOCTOR_INFO.title,
    subtitle: apiData.subtitle || DOCTOR_INFO.subtitle,
    fellowships: apiData.fellowships || DOCTOR_INFO.fellowships,
    specialization: apiData.specialization || DOCTOR_INFO.specialization,
    tagline: apiData.tagline || DOCTOR_INFO.tagline,
    experience: `${new Date().getFullYear() - 1991}+ years of experience`,
    experienceYears: new Date().getFullYear() - 1991,
    phone: apiData.phone || DOCTOR_INFO.phone,
    email: apiData.email || DOCTOR_INFO.email,
    whatsapp: apiData.whatsapp || DOCTOR_INFO.whatsapp,
    whatsappLink: apiData.whatsapp_link || DOCTOR_INFO.whatsappLink,
    hospital: apiData.hospital || DOCTOR_INFO.hospital,
    hospitalFull: apiData.hospital_full || DOCTOR_INFO.hospitalFull,
    address: apiData.address || DOCTOR_INFO.address,
    addressShort: apiData.address_short || DOCTOR_INFO.addressShort,
    timings: apiData.timings || DOCTOR_INFO.timings,
    rating: apiData.rating || DOCTOR_INFO.rating,
    reviewCount: apiData.review_count || DOCTOR_INFO.reviewCount,
    consultationLink: apiData.consultation_link || DOCTOR_INFO.consultationLink,
    googleMapsLink: apiData.google_maps_link || DOCTOR_INFO.googleMapsLink,
    doctorImage: apiData.doctor_image || DOCTOR_INFO.doctorImage,
    patients_treated: apiData.patients_treated || DOCTOR_INFO.patients_treated,
    surgeries_performed: apiData.surgeries_performed || DOCTOR_INFO.surgeries_performed,
    leadershipRoles: apiData.leadership_roles || DOCTOR_INFO.leadershipRoles,
    keyDistinctions: apiData.key_distinctions || DOCTOR_INFO.keyDistinctions,
    academicHighlights: apiData.academic_highlights || DOCTOR_INFO.academicHighlights,
    fellowshipDetails: apiData.fellowship_details || DOCTOR_INFO.fellowshipDetails,
    positions: apiData.positions || DOCTOR_INFO.positions,
    memberships: apiData.memberships || DOCTOR_INFO.memberships,
    keyProcedures: DOCTOR_INFO.keyProcedures,
    socialMedia: apiData.social_media || DOCTOR_INFO.socialMedia,
  };
};

export const SiteProvider = ({ children, forceSite = null }) => {
  const [siteConfig, setSiteConfig] = useState(() => {
    if (forceSite && SITE_CONFIGS[forceSite]) {
      return SITE_CONFIGS[forceSite];
    }
    return getSiteConfig();
  });
  
  // State for dynamic doctor info from API
  const [doctorInfo, setDoctorInfo] = useState(DOCTOR_INFO);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch doctor info from API on mount
  useEffect(() => {
    const loadDoctorInfo = async () => {
      try {
        const apiDoctorData = await fetchDoctorInfo();
        const transformedData = transformDoctorData(apiDoctorData);
        if (transformedData) {
          setDoctorInfo(transformedData);
        }
      } catch (error) {
        console.log('Using static doctor info (API unavailable):', error.message);
        // Keep using DOCTOR_INFO as fallback
      } finally {
        setIsLoading(false);
      }
    };
    
    loadDoctorInfo();
  }, []);

  // Apply theme CSS variables when site changes
  useEffect(() => {
    const theme = siteConfig.theme;
    const root = document.documentElement;
    
    // Update CSS custom properties
    root.style.setProperty('--primary', theme.primary);
    root.style.setProperty('--accent', theme.accent);
    root.style.setProperty('--ring', theme.primary);
    
    // NOTE: document.title and meta description are now managed by react-helmet-async
    // in each page component for better SEO control
    
    // Update theme color for mobile browsers
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) {
      themeColor.setAttribute('content', theme.primaryHex);
    }
  }, [siteConfig, doctorInfo.name]);

  // Function to switch sites (useful for preview/development)
  const switchSite = (siteId) => {
    if (SITE_CONFIGS[siteId]) {
      setSiteConfig(SITE_CONFIGS[siteId]);
    }
  };

  // Get theme utility classes
  const getThemeClasses = () => {
    const theme = siteConfig.theme;
    return {
      text: theme.textClass,
      bg: theme.bgClass,
      border: theme.borderClass,
      hover: theme.hoverBg,
      lightBg: theme.lightBg,
      gradient: theme.gradient,
      bgGradient: theme.bgGradient
    };
  };

  // Check if current site is the main hub
  const isMainSite = () => siteConfig.id === 'main';

  // Get related site configs
  const getRelatedSites = () => {
    return siteConfig.relatedSites?.map(siteId => SITE_CONFIGS[siteId]) || [];
  };
  
  // Function to refresh doctor info from API
  const refreshDoctorInfo = async () => {
    try {
      const apiDoctorData = await fetchDoctorInfo();
      const transformedData = transformDoctorData(apiDoctorData);
      if (transformedData) {
        setDoctorInfo(transformedData);
      }
    } catch (error) {
      console.error('Failed to refresh doctor info:', error);
    }
  };

  const value = {
    ...siteConfig,
    doctor: doctorInfo,
    theme: siteConfig.theme,
    allSites: SITE_CONFIGS,
    switchSite,
    getThemeClasses,
    isMainSite,
    getRelatedSites,
    isLoading,
    refreshDoctorInfo
  };

  return (
    <SiteContext.Provider value={value}>
      {children}
    </SiteContext.Provider>
  );
};

// Hook to use site context
export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

// Hook to get theme classes
export const useTheme = () => {
  const { theme, getThemeClasses } = useSite();
  return { theme, classes: getThemeClasses() };
};

export default SiteContext;
